import React from 'react'
import get from 'lodash/get'
import BellIcon from 'transfix-icons/build/BellIcon'
import CustomerIcon from 'transfix-icons/build/CustomerIcon'
import InsightsIcon from 'transfix-icons/build/InsightsIcon'
import PeopleIcon from 'transfix-icons/build/PeopleIcon'
import PriceIcon from 'transfix-icons/build/PriceIcon'
import RequirementsIcon from 'transfix-icons/build/RequirementsIcon'
import ShipmentsIcon from 'transfix-icons/build/ShipmentsIcon'
import TasksIcon from 'transfix-icons/build/TasksIcon'
import TrucksIcon from 'transfix-icons/build/TrucksIcon'
import WarehouseIcon from 'transfix-icons/build/WarehouseIcon'
import { IUserPermissions } from 'types'
import { getBackendUrl } from 'utils/api/apiHelpers'
import IssuesMenu from 'components/IssuesMenu'
import NotificationsMenu from 'components/NotificationsMenu'
import { ToolsMenu } from '../components'
import { filterRoutes } from './filterRoutes'
import {
  IGenerateDefaultRoutesOptions,
  getDefaultRouteUserOptions,
} from './getDefaultRouteUserOptions'

export interface INotificationsOptions {
  /**
   * For 'notifications' route
   */
  notifications?: number
  /**
   * For 'issues' route
   */
  escalatedIssues?: number
}

export const generateDefaultNewRoutes = (
  permissions: IUserPermissions | null,
  notifications: INotificationsOptions = {},
  options: IGenerateDefaultRoutesOptions = getDefaultRouteUserOptions()
) => {
  const { authToken, isAdmin, isCeRep, isCxRep, isNorthEastCmRep, isShipper } = options || {}

  if (!authToken || !permissions) {
    return filterRoutes([
      {
        location: 'login',
        name: 'Log In',
        display: true,
      },
    ])
  }

  const routes = filterRoutes([
    {
      location: 'task-assignments',
      name: 'My Tasks',
      display: isAdmin || isCeRep || isNorthEastCmRep || isCxRep,
      isLocked: !isAdmin,
      Icon: TasksIcon,
    },
    {
      location: 'issues',
      Icon: RequirementsIcon,
      type: 'link',
      name: 'My Issues',
      display: isAdmin,
      isLocked: !isAdmin,
      notificationCount: get(notifications, 'escalatedIssues'),
    },
    {
      location: 'booking',
      name: 'Shipments',
      display: isAdmin,
      isLocked: !isAdmin,
      Icon: ShipmentsIcon,
      childRoutes: [
        {
          location: 'booking',
          name: 'All Shipments',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'create-shipment',
          name: 'Create Shipment',
          display: isShipper || isAdmin,
          isLocked: !(isShipper || isAdmin),
        },
        {
          location: 'lane/assignment',
          name: 'Lane Assignments',
          display: isAdmin,
          isLocked: !isAdmin,
        },
      ],
    },
    {
      location: 'carriers',
      name: 'Carriers',
      display: isAdmin,
      isLocked: !isAdmin,
      Icon: TrucksIcon,
      childRoutes: [
        {
          location: 'carriers',
          name: 'All Carriers',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          name: 'Carrier Options',
          location: 'carrier-options',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'awarded-lanes',
          name: 'Awarded Lanes',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'lane',
          name: 'Manage Lanes',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'rfp-lanes',
          name: 'Sourced Rates',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'staples-headhaul-lanes',
          name: 'Staples Headhaul Lanes',
          display: isAdmin,
          isLocked: !isAdmin,
        },
      ],
    },
    {
      location: 'shippers',
      name: 'Shippers',
      display: isAdmin,
      isLocked: !isAdmin,
      Icon: WarehouseIcon,
      childRoutes: [
        {
          location: 'shippers',
          name: 'All Shippers',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'facilities',
          name: 'Facilities',
          display: isAdmin || isShipper,
        },
        {
          location: 'instant-rfp',
          name: 'Instant RFP',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'shipper-rate-matcher',
          name: 'Shipper Rate Matcher',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'shipper-rates',
          name: 'Shipper Rates',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'spot-control-panel',
          name: 'Spot Control Panel',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'spot-quote-tool',
          name: 'Spot Quote Tool',
          display: isAdmin,
          isLocked: !isAdmin,
        },
      ],
    },
    {
      location: 'edi-tools',
      name: 'EDI',
      Icon: InsightsIcon,
      display: isAdmin,
      isLocked: !isAdmin,
      childRoutes: [
        {
          location: 'edi-tools/connection-configs/as2',
          name: 'AS2 Connection Config',
          isLocked: true,
          display: isAdmin,
        },
        {
          location: 'edi-tools/connection-configs/inbound',
          name: 'Inbound Connection Config',
          isLocked: true,
          display: isAdmin,
        },
        {
          location: 'edi-tools/mapper-files',
          name: 'Mapper Config',
          isLocked: !isAdmin,
          display: isAdmin,
        },
        {
          location: 'edi-tools/connection-configs/outbound',
          name: 'Outbound Connection Config',
          isLocked: !isAdmin,
          display: isAdmin,
        },
        {
          location: 'edi-tools/self-service/inbound',
          name: 'Self Service Parser',
          isLocked: !isAdmin,
          display: isAdmin,
        },
        {
          location: 'edi-tools/self-service/outbound',
          name: 'Self Service Builder',
          isLocked: !isAdmin,
          display: isAdmin,
        },
      ],
    },
    {
      location: 'accounting',
      name: 'Accounting',
      Icon: PriceIcon,
      display: isAdmin,
      isLocked: !isAdmin,
      childRoutes: [
        {
          location: 'accounting-tools',
          name: 'Accounting Tool',
          display: isAdmin,
          isLocked: !isAdmin,
        },
      ],
    },
    {
      location: 'users',
      name: 'Users',
      display: isAdmin,
      isLocked: !isAdmin,
      Icon: PeopleIcon,
      childRoutes: [
        {
          location: 'users',
          name: 'All Users',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: 'groups',
          name: 'Groups',
          display: isAdmin,
          isLocked: !isAdmin,
        },
        {
          location: `${getBackendUrl('MARKETPLACE')}/authorization_admin`,
          name: 'Permissions',
          excludeBase: true,
          display: isAdmin,
          isLocked: !isAdmin,
        },
      ],
    },
    {
      location: '',
      type: 'icon',
      name: 'Tools',
      disableLink: true,
      display: isAdmin,
      isLocked: !isAdmin,
      Icon: CustomerIcon,
      MenuProps: {
        children: <ToolsMenu />,
      },
    },
    {
      location: 'task-assignments',
      name: 'Task Assignments',
      type: 'icon',
      display: isAdmin || isCeRep || isNorthEastCmRep || isCxRep,
      Icon: TasksIcon,
    },
    {
      location: 'issues',
      Icon: RequirementsIcon,
      type: 'icon',
      name: 'Issues',
      display: isAdmin,
      isLocked: !isAdmin,
      notificationCount: get(notifications, 'escalatedIssues'),
      MenuProps: {
        children: <IssuesMenu />,
      },
    },
    {
      location: 'notifications',
      disableLink: true,
      type: 'icon',
      name: 'Notifications',
      display: isAdmin,
      isLocked: !isAdmin,
      Icon: BellIcon,
      notificationCount: get(notifications, 'notifications'),
      MenuProps: {
        children: <NotificationsMenu />,
      },
    },
  ])

  return routes
}
